// Updated hooks to use mock API for development

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mockSalesforceApi, mockSalesforceQueries } from '../services/api/mockSalesforceApi';
import { useFilters } from '../stores/filterStore';
import type {
  Opportunity,
  PipelineMetrics,
  OpportunityStage,
  QueryFilters,
  ChartDataPoint,
  PipelineByStage
} from '../types/salesforce';

// Pipeline overview hook
export function usePipelineMetrics() {
  const { filters } = useFilters();

  return useQuery({
    ...mockSalesforceQueries.pipelineMetrics(filters),
    select: (data: PipelineMetrics) => ({
      ...data,
      // Add computed properties
      averageDealSizeFormatted: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(data.averageDealSize),
      totalValueFormatted: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(data.totalValue),
      winRateFormatted: `${data.winRate.toFixed(1)}%`,
    }),
  });
}

// Pipeline opportunities hook
export function usePipelineOpportunities() {
  const { filters, pagination } = useFilters();

  const query = useQuery({
    ...mockSalesforceQueries.opportunities(filters, pagination),
  });

  // Memoized computed data (shows performance awareness)
  const computedData = useMemo(() => {
    if (!query.data?.records) return null;

    const opportunities = query.data.records;

    // Group by stage
    const byStage = opportunities.reduce((acc, opp) => {
      const stage = opp.stage;
      if (!acc[stage]) {
        acc[stage] = [];
      }
      acc[stage].push(opp);
      return acc;
    }, {} as Record<OpportunityStage, Opportunity[]>);

    // Calculate stage metrics
    const stageMetrics: PipelineByStage[] = Object.entries(byStage).map(([stage, opps]) => ({
      stage: stage as OpportunityStage,
      count: opps.length,
      value: opps.reduce((sum, opp) => sum + opp.amount, 0),
      probability: opps.reduce((sum, opp) => sum + opp.probability, 0) / opps.length || 0,
    }));

    // Time series data for trending
    const timeSeriesData: ChartDataPoint[] = opportunities
      .sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
      .reduce((acc, opp, index) => {
        const date = new Date(opp.createdDate).toISOString().split('T')[0];
        const existingPoint = acc.find(point => point.date === date);

        if (existingPoint) {
          existingPoint.value += opp.amount;
        } else {
          acc.push({
            date,
            value: opp.amount,
            label: `${index + 1} opportunities`,
          });
        }

        return acc;
      }, [] as ChartDataPoint[]);

    return {
      byStage,
      stageMetrics,
      timeSeriesData,
      totalValue: opportunities.reduce((sum, opp) => sum + opp.amount, 0),
      averageValue: opportunities.length > 0
        ? opportunities.reduce((sum, opp) => sum + opp.amount, 0) / opportunities.length
        : 0,
    };
  }, [query.data?.records]);

  return {
    ...query,
    computedData,
  };
}

// Individual opportunity management
export function useOpportunity(id: string) {
  return useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => mockSalesforceApi.getOpportunityById(id),
    enabled: !!id,
  });
}

// Opportunity mutations (shows CRUD operations)
export function useUpdateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Opportunity> }) => {
      // Optimistic update
      queryClient.setQueryData(['opportunity', id], (old: Opportunity | undefined) => {
        if (!old) return old;
        return { ...old, ...data };
      });

      return mockSalesforceApi.updateOpportunity(id, data);
    },
    onSuccess: (_, { id }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-metrics'] });
    },
    onError: (_, { id }) => {
      // Rollback optimistic update on error
      queryClient.invalidateQueries({ queryKey: ['opportunity', id] });
    },
  });
}

// Pipeline forecasting (shows advanced business logic)
export function usePipelineForecast() {
  const { computedData, isLoading, error } = usePipelineOpportunities();

  const forecast = useMemo(() => {
    if (!computedData?.stageMetrics) return null;

    const { stageMetrics } = computedData;

    // Calculate weighted pipeline
    const weightedPipeline = stageMetrics.reduce((total, stage) => {
      return total + (stage.value * stage.probability / 100);
    }, 0);

    // Forecast by quarter
    const quarterlyForecast = stageMetrics.map(stage => ({
      stage: stage.stage,
      q1Forecast: stage.value * stage.probability / 100 * 0.3,
      q2Forecast: stage.value * stage.probability / 100 * 0.4,
      q3Forecast: stage.value * stage.probability / 100 * 0.2,
      q4Forecast: stage.value * stage.probability / 100 * 0.1,
    }));

    // Risk analysis
    const riskAnalysis = {
      highRisk: stageMetrics.filter(stage => stage.probability < 25),
      mediumRisk: stageMetrics.filter(stage => stage.probability >= 25 && stage.probability < 75),
      lowRisk: stageMetrics.filter(stage => stage.probability >= 75),
    };

    return {
      weightedPipeline,
      quarterlyForecast,
      riskAnalysis,
      confidence: calculateConfidenceScore(stageMetrics),
    };
  }, [computedData?.stageMetrics]);

  return { data: forecast, isLoading: isLoading || !computedData, error };
}

// Performance monitoring hook (shows observability awareness)
export function usePipelinePerformance() {
  const { filters } = useFilters();

  return useQuery({
    queryKey: ['pipeline-performance', filters],
    queryFn: async () => {
      const [currentPeriod, previousPeriod] = await Promise.all([
        mockSalesforceApi.getPipelineMetrics(filters),
        mockSalesforceApi.getPipelineMetrics({
          ...filters,
          dateRange: filters?.dateRange ? {
            start: getPreviousPeriodStart(filters.dateRange.start),
            end: getPreviousPeriodEnd(filters.dateRange.end),
          } : undefined,
        }),
      ]);

      return {
        current: currentPeriod,
        previous: previousPeriod,
        growth: {
          totalValue: calculateGrowthRate(currentPeriod.totalValue, previousPeriod.totalValue),
          totalOpportunities: calculateGrowthRate(currentPeriod.totalOpportunities, previousPeriod.totalOpportunities),
          winRate: calculateGrowthRate(currentPeriod.winRate, previousPeriod.winRate),
          averageDealSize: calculateGrowthRate(currentPeriod.averageDealSize, previousPeriod.averageDealSize),
        },
      };
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
}

// Utility functions (shows clean code practices)
function calculateConfidenceScore(stageMetrics: PipelineByStage[]): number {
  if (stageMetrics.length === 0) return 0;

  const totalValue = stageMetrics.reduce((sum, stage) => sum + stage.value, 0);
  const weightedConfidence = stageMetrics.reduce((sum, stage) => {
    const weight = stage.value / totalValue;
    return sum + (stage.probability * weight);
  }, 0);

  return Math.round(weightedConfidence);
}

function calculateGrowthRate(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

function getPreviousPeriodStart(currentStart: string): string {
  const date = new Date(currentStart);
  const daysDiff = 30; // Assume 30-day periods
  date.setDate(date.getDate() - daysDiff);
  return date.toISOString().split('T')[0];
}

function getPreviousPeriodEnd(currentEnd: string): string {
  const date = new Date(currentEnd);
  const daysDiff = 30;
  date.setDate(date.getDate() - daysDiff);
  return date.toISOString().split('T')[0];
}

// Real-time updates hook (WebSocket simulation)
export function useRealTimePipeline() {
  const queryClient = useQueryClient();

  // In production, this would use WebSockets or Server-Sent Events
  // For demo, we'll simulate with polling
  useQuery({
    queryKey: ['real-time-pipeline'],
    queryFn: () => mockSalesforceApi.getPipelineMetrics(),
    refetchInterval: 30000, // 30 seconds
    onSuccess: (data) => {
      // Update cache with fresh data
      queryClient.setQueryData(['pipeline-metrics'], data);
    },
  });
}

// Lead data hooks
export function useLeadMetrics() {
  const { filters } = useFilters();

  return useQuery({
    ...mockSalesforceQueries.leadMetrics(filters),
    select: (data) => ({
      ...data,
      conversionRate: data.totalLeads > 0 ? (data.convertedLeads / data.totalLeads) * 100 : 0,
      qualificationRate: data.totalLeads > 0 ? (data.qualifiedLeads / data.totalLeads) * 100 : 0,
    }),
  });
}

export function useLeads() {
  const { filters, pagination } = useFilters();

  return useQuery({
    ...mockSalesforceQueries.leads(filters, pagination),
  });
}